import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "../Layout/DashboardLayout";
import DashboardTopbar from "../Layout/DashboardTopbar";
import useTokenBalance from "../hooks/useTokenBalance";
import { useEngineConfigContext } from "../Context/EngineConfigContext";
import { useAuthContext } from "../Context/AuthContext";
import { useTranslation } from "react-i18next";
import { API_R3API_ODOO_EXECUTE_URI } from "../Context/Constants";
import { Auth } from "aws-amplify";
import axios from "axios";
import moment from "moment";
import TokenBalance from "./GetToken/TokenBalance";
import TokenOrderDetails from "./GetToken/TokenOrderDetails";
import TokenPaymentPanel from "./GetToken/TokenPaymentPanel";
import TokenAllPlans from "./GetToken/TokenAllPlans";
import {
  getPersistedGetTokenUiState,
  saveGetTokenPersistence
} from "../utils/getTokenPersistence";
import {
  extractProducts,
  findProductById,
  getProductDisplayName,
  getProductTokenCount,
  normalizeProductId,
  readNumericValue
} from "../utils/getTokenProductUtils";
import { fetchGetTokenResponse } from "../utils/getTokenApi";
import "./GetToken/get-token.css";

const GetToken = () => {
  const persistedUi = getPersistedGetTokenUiState();
  const { user } = useAuthContext();
  const { fetchInfo, currentConfig, setFormValues } = useEngineConfigContext();
  const { id } = useParams();
  const { t } = useTranslation("common");
  const [selectedProduct, setSelectedProduct] = useState(persistedUi.selectedProduct);
  const [productsLoading, setProductsLoading] = useState(persistedUi.products.length === 0);
  const [tokensLoading, setTokensLoading] = useState(persistedUi.totalQuantityRemaining === null);
  const [productsRefreshing, setProductsRefreshing] = useState(false);
  const [tokensRefreshing, setTokensRefreshing] = useState(false);
  const [productTax, setProductTax] = useState(0);
  const [quantity, setQuantity] = useState(0);
  const [total, setTotal] = useState(0);
  const [productPrice, setProductPrice] = useState(0);
  const [containtedQty, setContaintedQty] = useState(persistedUi.containtedQty);
  const [totalSelectedContainedQty, setTotalSelectedContainedQty] = useState(0);
  const [totalAmountWithoutTax, setTotalAmountWithoutTax] = useState(0);
  const [paypalResponse, setPaypalResponse] = useState(null);
  const [products, setProducts] = useState(persistedUi.products);
  const [totalQuantityRemaining, setTotalQuantityRemaining] = useState(
    persistedUi.totalQuantityRemaining
  );
  const initialContainedQtyRef = useRef(
    persistedUi.hasCache ? persistedUi.containtedQty : null
  );
  const productsRef = useRef(persistedUi.products);
  const selectedProductIdRef = useRef(persistedUi.selectedProduct?.id ?? null);
  const userTouchedProductRef = useRef(false);
  const { tokenBalance } = useTokenBalance();

  productsRef.current = products;

  const applySelectedProductPricing = (product, options = {}) => {
    if (!product) {
      setProductPrice("0.00");
      setQuantity(0);
      setTotal(0);
      setTotalSelectedContainedQty(0);
      setTotalAmountWithoutTax(0);
      setProductTax(0);
      return;
    }

    const price = readNumericValue(product.list_price, 0);
    const tax = readNumericValue(product.tax_amount, 0) / 100;
    const tokenQuantity = getProductTokenCount(product);
    const defaultContainedQty = readNumericValue(product.contained_quanity, 1);

    let nextContainedQty = defaultContainedQty;
    if (options.containedQty != null) {
      nextContainedQty = options.containedQty;
    } else if (initialContainedQtyRef.current !== null) {
      nextContainedQty = initialContainedQtyRef.current;
      initialContainedQtyRef.current = null;
    }

    const totalSelectedContainedQuantity = tokenQuantity * nextContainedQty;
    const totalAmtWithoutTax = nextContainedQty * price;
    const calculatedTotal = totalAmtWithoutTax * (1 + tax);

    setProductPrice(price.toFixed(2));
    setContaintedQty(nextContainedQty);
    setQuantity(tokenQuantity);
    setTotalAmountWithoutTax(totalAmtWithoutTax);
    setTotalSelectedContainedQty(totalSelectedContainedQuantity);
    setProductTax((tax * tokenQuantity).toFixed(2));
    setTotal(calculatedTotal.toFixed(2));
  };

  useEffect(() => {
    selectedProductIdRef.current = selectedProduct?.id ?? null;
    if (selectedProduct) {
      selectedProductIdRef.current = selectedProduct.id;
    }
  }, [selectedProduct]);

  useEffect(() => {
    if (persistedUi.selectedProduct) {
      applySelectedProductPricing(persistedUi.selectedProduct);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchInfo(id);
      return () => {
        setFormValues({});
      };
    }
  }, [id]);

  //   const updateFormValue = (key, value) => {
  //     setFormValues({ ...formValues, [key]: value });
  //   };

  useEffect(() => {
    if (currentConfig) {
      setFormValues({
        name: currentConfig.name,
        type: currentConfig.type,
        version: currentConfig.version,
        database: currentConfig.database,
        url: currentConfig.url,
        username: currentConfig.username,
        password: currentConfig.password,
        description: currentConfig.description
      });
    }
  }, [currentConfig]);

  //   const handleSubmit = async (e) => {
  //     e.preventDefault();
  //     if (id) {
  //       await update(id, formValues);
  //     } else {
  //       await create(formValues);
  //     }
  //     await fetchUserAttributes();
  //   };

  //   const handleDelete = async () => {
  //     if (confirm("Are you sure? You want to delete this engine configuration!")) {
  //       await destroy(id);
  //       await fetchUserAttributes();
  //     }
  //   };

  //   const testConfig = useCallback(async () => {
  //     const payload = {
  //       engineName: formValues.type,
  //       dbName: formValues.database,
  //       url: formValues.url,
  //       userName: formValues.username,
  //       token: formValues.password
  //     };
  //     await test(payload);
  //   }, [formValues, test]);

  useEffect(() => {
    saveGetTokenPersistence({
      selectedProductId: selectedProduct?.id ?? null,
      containtedQty,
      products,
      totalQuantityRemaining
    });
  }, [selectedProduct, containtedQty, products, totalQuantityRemaining]);
  async function sendTransactionDetails(paypalResponse) {
    const session = await Auth.currentSession();
    const idTokenTrans = session.getIdToken().getJwtToken();
    //const accessToken = session.getAccessToken().getJwtToken();
    let u = await Auth.currentAuthenticatedUser();
    const email = u.attributes.email;
    const LOT_UTC = moment().utc().format("YYYY-MM-DD HH:mm:ss");
    let expirationTimeframe = selectedProduct?.expiration_timeframe;
    const LOT_UTC_Expiration = moment()
      .utc()
      .add(expirationTimeframe, "months")
      .format("YYYY-MM-DD HH:mm:ss");
    const LOT_Local_Time = moment().format("YYYY-MM-DD HH:mm:ss");
    const LOTNameDate = moment().format("YYYYMMDD_HH:mm:ss");
    const LOT_Name = email + "_" + LOTNameDate;

    const passingData = {
      action: "createquotation",
      domain: process.env.REACT_APP_DOMAIN_PARAM,
      emailid: email,
      company: false,
      input: {
        product_id: selectedProduct?.id,
        product_category: selectedProduct?.product_category,
        quantity: quantity,
        paidAmount: total,
        unit_of_measure: selectedProduct?.unit_of_measure,
        contained_quanity: containtedQty,
        total_quantity: parseInt(quantity) * parseInt(containtedQty),
        LOT_UTC: LOT_UTC,
        LOT_UTC_Expiration: LOT_UTC_Expiration,
        LOT_Local_Time: LOT_Local_Time,
        LOT_Name: LOT_Name,
        description: paypalResponse
      }
    };
    console.log("passing data", passingData);
    await axios
      .post(
        API_R3API_ODOO_EXECUTE_URI,
        {
          action: "createquotation",
          domain: process.env.REACT_APP_DOMAIN_PARAM,
          emailid: email,
          company: false,
          input: {
            product_id: selectedProduct?.id,
            product_category: selectedProduct?.product_category,
            quantity: quantity,
            paidAmount: total,
            unit_of_measure: selectedProduct?.unit_of_measure,
            contained_quanity: containtedQty,
            total_quantity: parseInt(quantity) * parseInt(containtedQty),
            LOT_UTC: LOT_UTC,
            LOT_UTC_Expiration: LOT_UTC_Expiration,
            LOT_Local_Time: LOT_Local_Time,
            LOT_Name: LOT_Name,
            description: paypalResponse
          }
        },
        {
          headers: {
            Authorization: `Bearer ${idTokenTrans}`,
            "Content-Type": "application/json"
          }
        }
      )
      .then(async function (response) {
        console.log("createonhand", response.data);
        if (response.data.return_code == 0) {
          //setShowQuotationPopup(true);
          console.log("Invoice>>", response.data.invoice.s3path);
          //setBucket(response.data.invoice.bucket_name);
          //setKey(response.data.invoice.s3path);
          //setFilename(response.data.invoice.filename);
          localStorage.setItem("outputfilepath", response.data.outputfilepath);
          localStorage.setItem("bucket", response.data.invoice.bucket_name);
          try {
            const { email: tokenEmail, headers } = await getAuthRequestContext();
            await getAvailableTokens(tokenEmail, headers);
          } catch (tokenError) {
            console.error("Error refreshing available tokens:", tokenError.message);
          }
        }
        //setProduct(response.data);
      })
      .catch(function (error) {
        console.error("Error:", error.message);
      })
      .then(function () {});
  }
  //   async function sendTransactionDetails(paypalResponse) {
  //     const session = await Auth.currentSession();
  //     const idTokenTrans = session.getIdToken().getJwtToken();
  //     const accessToken = session.getAccessToken().getJwtToken();
  //     let u = await Auth.currentAuthenticatedUser();
  //     const email = u.attributes.email;

  //     await axios
  //       .post(
  //         API_SERVER + "sendTransactionDetails",
  //         {
  //           email: email,
  //           paypalResponse: paypalResponse,
  //           product_id: selectedProduct?.id,
  //           productExpirationTimeframe: selectedProduct?.expiration_timeframe,
  //           quantity: quantity,
  //           amount: total,
  //           unit_of_measure: selectedProduct?.unit_of_measure,
  //           contained_quanity: containtedQty,
  //           authToken: idTokenTrans
  //         },
  //         {
  //           headers: {
  //             Authorization: `Bearer ${accessToken}`,
  //             "Content-Type": "application/json"
  //           }
  //         }
  //       )
  //       .then(function (response) {
  //         console.log("createonhand", response.data);
  //         if (response.data.return_code == 0) {
  //           //setShowQuotationPopup(true);
  //           console.log("Invoice>>", response.data.invoice.s3path);
  //           //setBucket(response.data.invoice.bucket_name);
  //           //setKey(response.data.invoice.s3path);
  //           //setFilename(response.data.invoice.filename);
  //           localStorage.setItem("outputfilepath", response.data.outputfilepath);
  //           localStorage.setItem("bucket", response.data.invoice.bucket_name);
  //         }
  //         //setProduct(response.data);
  //       })
  //       .catch(function (error) {
  //         console.error("Error:", error.message);
  //       })
  //       .then(function () {});
  //   }
  //   async function getProductList() {
  //     const session = await Auth.currentSession();
  //     const idTokenProd = session.getIdToken().getJwtToken();
  //     const accessToken = session.getAccessToken().getJwtToken();
  //     //const userInfo = await getLoggedInUserEmail();
  //     let u = await Auth.currentAuthenticatedUser();
  //     const email = u.attributes.email;
  //     //setLoading(true);
  //     await axios
  //       .post(
  //         API_SERVER + "odooProductList",
  //         {
  //           action: "getproductlist",
  //           domain: "prestocharts.com",
  //           authToken: idTokenProd,
  //           email: email,
  //           company: false,
  //           companyname: "",
  //           region: ""
  //         },
  //         {
  //           headers: {
  //             Authorization: `Bearer ${accessToken}`,
  //             "Content-Type": "application/json"
  //           }
  //         }
  //       )
  //       .then(function (response) {
  //         console.log("Product list", response.data);
  //         //setProduct(response.data);

  //         if (response.data.length > 0) {
  //           //setSelectedProduct(response.data[0]);
  //           //let firstProductSelected = response.data[0];
  //           //onQtyChange(firstProductSelected.number_of_tokens);
  //         }
  //       })
  //       .catch(function (error) {
  //         console.error("Error:", error.message);
  //       })
  //       .then(function () {
  //         //setLoading(false);
  //       });
  //   }
  const selectProductById = (productId) => {
    if (productId == null || productId === "") {
      userTouchedProductRef.current = false;
      setSelectedProduct(null);
      applySelectedProductPricing(null);
      return;
    }

    userTouchedProductRef.current = true;
    initialContainedQtyRef.current = null;
    const product = findProductById(productsRef.current, productId);
    setSelectedProduct(product);
    applySelectedProductPricing(product);
  };

  const selectedProductFunc = (event) => {
    selectProductById(event.target.value);
  };

  const formatProductOption = (product) => {
    const name = getProductDisplayName(product);
    const tokens = getProductTokenCount(product);
    const price = readNumericValue(product.list_price, 0).toFixed(2);
    return `${name} — ${tokens.toLocaleString()} tokens / $${price}`;
  };

  const taxRate = useMemo(() => {
    return selectedProduct ? readNumericValue(selectedProduct.tax_amount, 0) : 0;
  }, [selectedProduct]);

  const unitOfMeasure =
    selectedProduct?.unit_of_measure || selectedProduct?.uom_name || "";

  const onContaintedQtyChange = (event) => {
    if (!selectedProduct) return;

    const newContainedQuantity = parseInt(event.target.value, 10) || 1;
    applySelectedProductPricing(selectedProduct, {
      containedQty: newContainedQuantity
    });
  };

  useEffect(() => {
    console.log("paypalResponse", paypalResponse);
    if (paypalResponse && paypalResponse.status === "COMPLETED") {
      console.log("paypal transaction completed");
      sendTransactionDetails(paypalResponse);
    }
  }, [paypalResponse]);

  //   useEffect(() => {
  //     getProductList();
  //   }, []);

  const getAuthRequestContext = async () => {
    const session = await Auth.currentSession();
    const idTokenTrans = session.getIdToken().getJwtToken();
    const u = await Auth.currentAuthenticatedUser();
    return {
      idTokenTrans,
      email: u.attributes.email,
      headers: {
        Authorization: `Bearer ${idTokenTrans}`,
        "Content-Type": "application/json"
      }
    };
  };

  const getAvailableTokens = async (email, headers) => {
    const data = await fetchGetTokenResponse(email, headers);
    const remaining = readNumericValue(data?.totalquantityremaining, 0);
    setTotalQuantityRemaining(remaining);
    console.log("Available tokens:", remaining, data);
    return data;
  };

  const extractProductsFromResponse = (responseData) => extractProducts(responseData);

  const fetchProducts = async (email, headers, preferredProductId = null) => {
    const resp = await axios.post(
      API_R3API_ODOO_EXECUTE_URI,
      {
        action: "getproductlist",
        domain: process.env.REACT_APP_DOMAIN_PARAM,
        emailid: email,
        company: false,
        companyname: "",
        region: ""
      },
      { headers }
    );
    const productList = extractProductsFromResponse(resp.data).filter(
      (product) => product?.product_category === "Token"
    );
    productsRef.current = productList;
    setProducts(productList);

    const targetId = userTouchedProductRef.current
      ? selectedProductIdRef.current
      : preferredProductId ?? selectedProductIdRef.current;

    const nextProduct =
      findProductById(productList, targetId) || productList[0] || null;

    setSelectedProduct(nextProduct);
    applySelectedProductPricing(nextProduct);

    console.log("Product list", productList, "raw:", resp.data);
    return productList;
  };

  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;

    const loadInitialData = async () => {
      const hasProductCache = persistedUi.products.length > 0;
      const hasTokenCache = persistedUi.totalQuantityRemaining !== null;

      if (hasProductCache) {
        setProductsRefreshing(true);
      } else {
        setProductsLoading(true);
      }

      if (hasTokenCache) {
        setTokensRefreshing(true);
      } else {
        setTokensLoading(true);
      }

      try {
        const { email, headers } = await getAuthRequestContext();
        const preferredProductId = selectedProduct?.id ?? persistedUi.selectedProductId;
        const [tokensResult, productsResult] = await Promise.allSettled([
          getAvailableTokens(email, headers),
          fetchProducts(email, headers, preferredProductId)
        ]);
        if (tokensResult.status === "rejected") {
          console.error("getAvailableTokens failed:", tokensResult.reason);
        }
        if (productsResult.status === "rejected") {
          console.error("fetchProducts failed:", productsResult.reason);
        }
      } finally {
        if (!cancelled) {
          setProductsLoading(false);
          setTokensLoading(false);
          setProductsRefreshing(false);
          setTokensRefreshing(false);
        }
      }
    };

    loadInitialData();
    return () => {
      cancelled = true;
    };
  }, [user]);
  return (
    <DashboardLayout>
      <div className="dashboard-page get-token-page">
        <DashboardTopbar title={t("Get Token")} tokenBalance={tokenBalance} />

        <div className="page-content">
          <div className="page-header">
            <h1>{t("Purchase Tokens")}</h1>
            <p>
              {t(
                "Select a plan, review totals, and complete payment securely via PayPal"
              )}
            </p>
          </div>

          <div className="get-token-layout">
            <div>
              {tokensLoading && totalQuantityRemaining === null ? (
                <div className="dashboard-loading get-token-section-loading">
                  {t("Fetching...")}
                </div>
              ) : (
                <TokenBalance
                  balance={totalQuantityRemaining}
                  updating={tokensRefreshing}
                />
              )}
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-header">
                  <div className="card-title">{t("Select Product")}</div>
                  {productsRefreshing ? (
                    <span className="get-token-inline-status">{t("Updating...")}</span>
                  ) : null}
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor="productSelect">
                    {t("Product Plan")}
                  </label>
                  {productsLoading && products.length === 0 ? (
                    <div className="dashboard-loading get-token-section-loading">
                      {t("Fetching...")}
                    </div>
                  ) : (
                    <select
                      id="productSelect"
                      className="form-control"
                      value={selectedProduct ? normalizeProductId(selectedProduct.id) : ""}
                      onChange={selectedProductFunc}
                      disabled={productsLoading && products.length === 0}>
                      <option value="">{t("SelectProduct")}</option>
                      {Array.isArray(products) &&
                        products.map((product1) => (
                          <option key={product1.id} value={normalizeProductId(product1.id)}>
                            {formatProductOption(product1)}
                          </option>
                        ))}
                    </select>
                  )}
                </div>
              </div>

              <TokenOrderDetails
                containtedQty={containtedQty}
                unitOfMeasure={unitOfMeasure}
                productPrice={productPrice}
                quantity={quantity}
                totalSelectedContainedQty={totalSelectedContainedQty}
                totalAmountWithoutTax={totalAmountWithoutTax}
                productTax={productTax}
                total={total}
                onContainedQtyChange={onContaintedQtyChange}
              />
            </div>

            <div className="get-token-payment-col">
              <TokenPaymentPanel
                selectedProduct={selectedProduct}
                totalSelectedContainedQty={totalSelectedContainedQty}
                totalAmountWithoutTax={totalAmountWithoutTax}
                productTax={productTax}
                total={total}
                taxRate={taxRate}
                paypalResponse={paypalResponse}
                setPaypalResponse={setPaypalResponse}
                loading={productsLoading && products.length === 0}
              />
              <TokenAllPlans
                products={products}
                selectedProductId={selectedProduct?.id}
                onSelectProduct={selectProductById}
                loading={productsLoading && products.length === 0}
              />
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};export default GetToken;
